import type { PackageUpdatePayload } from '@ms-cloudpack/api-server/browser';
import { removeHashFromUrl } from '@ms-cloudpack/path-string-parsing';

/**
 * Handles hot module replacement (HMR) updates for packages.
 *
 * This function is called when the development server notifies the client that a package
 * has been updated. It performs the following steps:
 *
 * 1. **Check which modules are loaded**: Uses Performance API to determine which entry points
 *    from the package are actually loaded on the current page.
 *
 * 2. **Re-import updated modules**: Dynamically imports the new versions of loaded modules
 *    using their updated bundle URLs (with new hash).
 *
 * 3. **Trigger React Refresh**: If React Refresh is available, triggers component updates
 *    while preserving component state. Falls back to full page reload otherwise.
 *
 * The process ensures:
 * - Only relevant modules are updated (skip modules not loaded on current page)
 * - React component state is preserved when possible
 * - Graceful fallback to full reload if React Refresh fails
 *
 * @param data - The package update payload containing package name, version, and entry points
 *
 * @example
 * // This is typically called by the WebSocket message handler:
 * socket.on('package-update', (data) => {
 *   await handlePackageUpdate(data);
 * });
 */
export const handlePackageUpdate = async (data: unknown): Promise<void> => {
  const { packageName, entryPoints, version } = data as PackageUpdatePayload;

  try {
    console.log(`[Cloudpack HMR] Package updated: ${packageName}@${version}`);

    // Get all loaded resources for checking
    const normalizedResources = (performance.getEntriesByType('resource') as PerformanceResourceTiming[]).map((r) =>
      removeHashFromUrl(r.name),
    );

    // Process each entry point
    let loadedCount = 0;
    for (const { importPath, bundleUrl } of entryPoints) {
      // Check if this module was actually loaded using Performance API
      // bundleUrl already has the new hash: http://localhost:5500/pkg@1.0.0/h-abc123-1234567890/v1.2/bundled/lib/file.js
      // We normalize both URLs by removing the hash to compare them
      // If bundleUrl is relative (starts with /), prepend the origin to make it absolute
      const absoluteBundleUrl = bundleUrl.startsWith('/') ? `${window.location.origin}${bundleUrl}` : bundleUrl;
      const normalizedBundleUrl = removeHashFromUrl(absoluteBundleUrl);
      const wasLoaded = normalizedResources.some((r) => r === normalizedBundleUrl);

      if (!wasLoaded) {
        console.log(`[Cloudpack HMR] Skipping ${importPath} - not loaded on this page`);
        continue;
      }

      console.log(`[Cloudpack HMR] Loading ${importPath} from ${absoluteBundleUrl}`);

      // Dynamic import - use webpackIgnore to bypass bundler's import analysis
      // This ensures we use the browser's native import() for runtime URL
      await import(/* webpackIgnore: true */ absoluteBundleUrl);
      loadedCount++;
    }

    if (loadedCount === 0) {
      console.log(`[Cloudpack HMR] No entry points from ${packageName} were loaded on this page`);
      return;
    }

    // ===== Trigger React Refresh =====
    //
    // React Refresh is a hot reloading feature that updates React components without losing state.
    // It allows developers to see component changes instantly while preserving:
    // - Component state (useState, useReducer)
    // - Form inputs
    // - Scroll position
    // - Any other runtime state
    //
    // How it works:
    // 1. When modules are re-imported above, React Refresh automatically detects changed components
    // 2. Components are registered via $RefreshReg$ calls (injected by the bundler during build)
    // 3. Calling performReactRefresh() re-renders the registered components with new code
    // 4. React's reconciliation preserves state by matching component types and positions
    //
    // The $RefreshRuntime$ global is initialized in registerReactRefresh.ts
    if (window.$RefreshRuntime$?.performReactRefresh) {
      // Perform the actual refresh - this will update all components that were re-registered
      // during the dynamic import above, while preserving their state
      const refreshResult = window.$RefreshRuntime$.performReactRefresh();

      if (refreshResult === null) {
        // Refresh returned null - this indicates no components were registered
        // This shouldn't happen if we successfully imported modules, so we fall back to full reload
        console.warn(`[Cloudpack HMR] React Refresh returned null despite registrations, reloading page`);
        window.location.reload();
      } else {
        // React components successfully updated with state preservation
        // refreshResult contains metadata about what was updated
        console.log(`[Cloudpack HMR] ✓ Updated ${packageName}@${version}`, refreshResult);
      }
    } else {
      // React Refresh runtime is not available
      // This happens when:
      // - React Refresh wasn't initialized (registerReactRefresh.ts not called)
      // - The app doesn't use React
      // - React Refresh was disabled
      // Fall back to full page reload to show the changes
      console.warn(`[Cloudpack HMR] React Refresh runtime not found, reloading page`);
      window.location.reload();
    }
  } catch (error) {
    console.error(`[Cloudpack HMR] ✗ Failed to update ${packageName}:`, error);
    window.location.reload();
  }
};
